import {DataStatus, NotificationCenter, NotificationItemData} from 'platform/components';
import {ValueByDevice} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  ListSentToastNotificationsResponseBody,
  usePatchSentToastNotificationMutation,
  usePatchUnreadSentToastNotificationsMutation,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {useGetNotificationItemData} from '../hooks/useGetNotificationItemData';

interface NotificationsWidgetProps extends TestIdProps {
  notificationData?: ListSentToastNotificationsResponseBody;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  pageSize?: number;
  isHeaderHidden?: boolean;
  isDisabled?: boolean;
  onNotificationClick?: () => void;
  columns?: number | ValueByDevice<number>;
}

export function NotificationsWidget(props: NotificationsWidgetProps) {
  const navigate = useNavigate();

  const {data: userInfo} = useGetCurrentUserInfoQuery();

  const [notifications, setNotifications] = useState<NotificationItemData[]>();
  const [isLoadingNotificationData, setIsLoadingNotificationData] = useState(true);

  const [patchNotification, {isLoading: isPatching}] = usePatchSentToastNotificationMutation();
  const [patchAllUnreadNotifications, {isLoading: isAllPatching}] =
    usePatchUnreadSentToastNotificationsMutation();

  const handleMarkAsRead = (id: string) => {
    patchNotification({id, body: {isRead: true}})
      .unwrap()
      .catch(handleApiError);
  };

  const getNotificationItemData = useGetNotificationItemData({
    onNotificationClick: props.onNotificationClick,
    handleMarkAsRead,
  });

  useEffect(
    function loadNotificationItems() {
      if (isNil(userInfo)) {
        return;
      }

      const notifications = props.notificationData?.notifications
        .map((notification) => getNotificationItemData(notification, userInfo.id))
        .filter(isNotNil);

      Promise.all(notifications ?? []).then((notifications) => {
        setNotifications(notifications);
        setIsLoadingNotificationData(false);
      });
    },
    [getNotificationItemData, props.notificationData?.notifications, userInfo]
  );

  const handleMarkAllAsRead = () => {
    patchAllUnreadNotifications({body: {isRead: true}})
      .unwrap()
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isError={props.isError}
      isLoading={props.isLoading || isLoadingNotificationData}
      isEmpty={isNilOrEmpty(notifications)}
      emptyMessage={i18n.t('general.notifications.labels.empty')}
      minHeight={80}
    >
      <NotificationCenter
        isDisabled={
          props.isDisabled || isAllPatching || isPatching || props.isLoading || props.isFetching
        }
        onMarkAllAsRead={handleMarkAllAsRead}
        onMarkAsRead={handleMarkAsRead}
        notifications={notifications}
        onNavigate={navigate}
        onNotificationClick={props.onNotificationClick}
        isHeaderHidden={props.isHeaderHidden}
        data-testid={suffixTestId('notificationCenter', props)}
        columns={props.columns}
      />
    </DataStatus>
  );
}
